import { Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from '../context/AuthContext';

const primaryNav = [
  { label: "Home", href: "/" },
  { label: "Identity CoE", href: "/identity-coe" },
  { label: "Why Identity Matters", href: "/why-identity-matters" },
  { label: "Capabilities", href: "/capabilities" },
  { label: "Architecture", href: "/architecture" },
  { label: "Offerings", href: "/offerings" },
  { label: "ROI & TCO", href: "/roi" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
];

const industryNav = [
  { label: "Government", href: "/industries/government" },
  { label: "BFSI (Banking & Finance)", href: "/industries/bfsi" },
  { label: "Digital & Critical Infrastructure", href: "/industries/infrastructure" },
  { label: "Digital Natives", href: "/industries/digital-natives" },
];

function isActivePath(pathname, href) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

export default function Header() {
  const location = useLocation();
  const pathname = location.pathname;
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const industriesRef = useRef(null);

  const activeMap = useMemo(() => {
    const map = new Map();
    for (const item of primaryNav) map.set(item.href, isActivePath(pathname, item.href));
    for (const item of industryNav) map.set(item.href, isActivePath(pathname, item.href));
    return map;
  }, [pathname]);

  useEffect(() => {
    if (!industriesOpen) return;

    function onPointerDown(event) {
      const target = event.target;
      if (!target) return;
      if (industriesRef.current && industriesRef.current.contains(target)) return;
      setIndustriesOpen(false);
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setIndustriesOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [industriesOpen]);

  return (
    <header className="sticky top-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <Link
              to="/"
              className="group flex items-center gap-3 rounded-xl px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60"
              aria-label="Identity, Data & Key Security Centre of Excellence"
            >
              <div className="leading-tight">
                <div className="font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
                  Identity Security CoE
                </div>
                <div className="text-xs text-[var(--text-secondary)]">Vault of Digital Trust</div>
              </div>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
              {primaryNav.slice(0, 4).map((item) => {
                const active = activeMap.get(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`rounded-xl px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60 ${
                      active
                        ? "bg-white/[0.06] text-[var(--text-primary)]"
                        : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              <div className="relative" ref={industriesRef}>
                <button
                  type="button"
                  className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60 ${
                    pathname.startsWith("/industries")
                      ? "bg-white/[0.06] text-[var(--text-primary)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  }`}
                  aria-haspopup="menu"
                  aria-expanded={industriesOpen}
                  onClick={() => setIndustriesOpen((v) => !v)}
                >
                  Verticals
                  <span className="text-xs opacity-80">▾</span>
                </button>

                {industriesOpen ? (
                  <div
                    role="menu"
                    className="absolute left-0 mt-2 w-[320px] overflow-hidden rounded-2xl border border-white/10 bg-[var(--bg-secondary)]/80 shadow-2xl backdrop-blur-xl"
                  >
                    <div className="p-2">
                      {industryNav.map((item) => {
                        const active = activeMap.get(item.href);
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            role="menuitem"
                            className={`block rounded-xl px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60 ${
                              active
                                ? "bg-white/[0.06] text-[var(--text-primary)]"
                                : "text-[var(--text-secondary)] hover:bg-white/[0.04] hover:text-[var(--text-primary)]"
                            }`}
                            onClick={() => setIndustriesOpen(false)}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

              {primaryNav.slice(4, 9).map((item) => {
                const active = activeMap.get(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`rounded-xl px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60 ${
                      active
                        ? "bg-white/[0.06] text-[var(--text-primary)]"
                        : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <Link
                to={user ? "/dashboard" : "/login"}
                className="hidden rounded-xl bg-[var(--accent-trust)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:translate-y-[-1px] focus:outline-none focus:ring-2 focus:ring-[var(--accent-cyan)]/60 sm:inline-flex"
              >
                {user ? "Dashboard" : "Login"}
              </Link>

              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-[var(--text-primary)] lg:hidden"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                onClick={() => setMobileOpen((v) => !v)}
              >
                <span className="text-lg leading-none">{mobileOpen ? "×" : "≡"}</span>
              </button>
            </div>
          </div>

          {mobileOpen ? (
            <div className="border-t border-white/10 px-4 py-4 sm:px-6 lg:hidden">
              <div className="grid gap-1">
                {primaryNav.map((item) => {
                  const active = activeMap.get(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`rounded-xl px-3 py-2 text-sm transition-colors ${
                        active
                          ? "bg-white/[0.06] text-[var(--text-primary)]"
                          : "text-[var(--text-secondary)] hover:bg-white/[0.04] hover:text-[var(--text-primary)]"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                })}

                <div className="my-2 h-px bg-white/10" />

                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  Industries
                </div>
                {industryNav.map((item) => {
                  const active = activeMap.get(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`rounded-xl px-3 py-2 text-sm transition-colors ${
                        active
                          ? "bg-white/[0.06] text-[var(--text-primary)]"
                          : "text-[var(--text-secondary)] hover:bg-white/[0.04] hover:text-[var(--text-primary)]"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                })}

                <div className="mt-4">
                  <Link
                    to={user ? "/dashboard" : "/login"}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-4 py-2 text-sm font-semibold text-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {user ? "Dashboard" : "Login"}
                  </Link>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
